import { useState } from 'react';
import { CalendarClock, ShieldCheck, Undo2 } from 'lucide-react';
import {
  billedTileLabel,
  cancellationDate,
  formatMoney,
  intervalSuffix,
  HOUSEHOLD_LABELS,
  LABEL,
} from './helper';

const Fact = ({ label, value }) => (
  <div className="rounded-xl border border-amber-100 bg-white px-4 py-3 text-center">
    <p className={LABEL}>{label}</p>
    <p className="mt-1 text-sm font-bold text-secondary-900">{value}</p>
  </div>
);

const Line = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 px-3.5 py-2.5">
    <span className="text-xs font-medium text-secondary-500">{label}</span>
    <span className="text-xs font-bold text-secondary-900">{value}</span>
  </div>
);

// Owns every cancelled-but-still-running membership. The celebratory member
// panel buries the one thing that matters here — the date member pricing stops
// — so once a cancellation is scheduled that becomes the headline, whether the
// patient moved to insurance or back to paying for themselves.
const MembershipEndingNotice = ({ enrollment, onKeep, insured = false, busy = false }) => {
  const [confirming, setConfirming] = useState(false);
  const {
    plan_name, household_type, interval, price_dollars, manageable = true, subscriber_name,
  } = enrollment || {};
  const endsOn = cancellationDate(enrollment);

  return (
    <div className="animate-slide-up rounded-2xl border border-amber-100 bg-amber-50/50 p-6 sm:p-8">
      <div className="flex flex-col items-center text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500 text-white shadow-lg shadow-amber-200/50">
          <CalendarClock size={26} strokeWidth={2.5} />
        </span>

        <h3 className="mt-4 text-xl font-bold text-secondary-900">
          Your {plan_name} membership is cancelled
        </h3>

        <p className="mt-1.5 max-w-md text-xs leading-relaxed text-secondary-600">
          You keep member pricing until <strong>{endsOn}</strong> and{' '}
          <strong>you won't be charged again</strong>.{' '}
          {insured
            ? 'After that your insurance takes over.'
            : "After that you'll be billed directly at standard prices."}
        </p>

        <div className="mt-5 grid w-full max-w-xl grid-cols-1 gap-3 sm:grid-cols-3">
          <Fact label="Plan" value={`${plan_name} · ${HOUSEHOLD_LABELS[household_type] || household_type}`} />
          <Fact label="Member pricing until" value={endsOn} />
          <Fact label="Then" value={insured ? 'Your insurance' : 'Standard prices'} />
        </div>

        {manageable && onKeep && (
          <div className="mt-6 w-full max-w-xl rounded-xl border border-secondary-200 bg-white px-4 py-4">
            <p className="text-sm font-bold text-secondary-900">
              {confirming ? 'Are you sure you want to restart this membership?' : 'Changed your mind?'}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-secondary-500">
              {confirming
                ? 'Your card on file goes back on automatic billing. Please check the details before you confirm.'
                : insured
                  ? `Keeping your membership means going back to member pricing — so your insurance comes off file and billing resumes on ${endsOn}.`
                  : `Nothing is charged today — billing simply resumes on ${endsOn} and your member pricing carries on uninterrupted.`}
            </p>

            {/* Resuming still puts the card back on the hook for recurring charges,
                so both paths confirm — the insured one additionally warns that
                it takes the plan they just added back off file. */}
            {confirming && (
              <>
                <div className="mt-3 divide-y divide-secondary-100 rounded-xl border border-secondary-200 bg-secondary-50/60">
                  <Line
                    label="Plan"
                    value={`${plan_name} · ${HOUSEHOLD_LABELS[household_type] || household_type}`}
                  />
                  <Line
                    label={billedTileLabel(interval)}
                    value={`${formatMoney(price_dollars)} ${intervalSuffix(interval)}`}
                  />
                  <Line label="Next charge" value={endsOn} />
                  {insured && <Line label="Your insurance" value="Comes off file today" />}
                </div>

                <p className="mt-2.5 text-xs leading-relaxed text-secondary-500">
                  Nothing is charged today. Your saved card is charged{' '}
                  <strong>{formatMoney(price_dollars)}</strong> on <strong>{endsOn}</strong> and
                  renews automatically after that — you can cancel again at any time before then.
                </p>
              </>
            )}

            {confirming ? (
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={onKeep}
                  disabled={busy}
                  className="btn h-10 min-h-10 flex-1 gap-2 rounded-xl border-0 bg-secondary-900 text-xs font-semibold text-white hover:bg-secondary-800 disabled:opacity-50"
                >
                  <ShieldCheck size={14} strokeWidth={2.5} />
                  {busy
                    ? insured ? 'Switching back…' : 'Restarting…'
                    : insured
                      ? 'Yes — keep membership, remove insurance'
                      : 'Yes — restart my membership'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                  className="btn h-10 min-h-10 gap-2 rounded-xl border border-secondary-200 bg-white px-4 text-xs font-semibold text-secondary-700 hover:bg-secondary-50 disabled:opacity-50"
                >
                  Never mind
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                disabled={busy}
                className="btn mt-3 h-10 min-h-10 w-full gap-2 rounded-xl border border-secondary-200 bg-white text-xs font-semibold text-secondary-900 hover:bg-secondary-50 disabled:opacity-50 sm:w-auto sm:px-5"
              >
                <Undo2 size={14} strokeWidth={2.5} />
                Keep my membership
              </button>
            )}
          </div>
        )}
        {/* A covered dependant still needs the date and the reassurance —
            they just can't be the one to reverse it. */}
        {!manageable && (
          <p className="mt-5 max-w-md text-xs text-secondary-500">
            {subscriber_name || 'The subscriber'} cancelled this membership and is the only one
            who can restart it.
          </p>
        )}
      </div>
    </div>
  );
};

export default MembershipEndingNotice;
